import { isBrowser } from 'ws-scripts/modules/environment';
import { SET_MEDIA } from './types';
import { NO_PHOTOS_IMAGE } from '../helpers/no-photos';
import { setRenderSettings } from './renderSettings';
import { setModalSettings } from './modalSettings';
import { sortVideos } from '../helpers/videos';

const GALLERY_TYPES = ['image', 'html'];

const parseLimitString = (paramString) => {
	let limit = null;
	try {
		if (paramString.includes('_limitVehicleMedia=')) {
			limit = parseInt(
				paramString.match(/_limitVehicleMedia=(\d+)/)[1],
				10
			);
		}
	} catch (_e) {
		/* Purposefully do nothing */
	}
	return limit;
};

const parseLimitFromLocation = () => {
	return isBrowser ? parseLimitString(document.location.search) : null;
};

const insertWIAPIContent = (imagesToDisplay, galleryContent) => {
	const toItem = (obj) => {
		return {
			vehicle: galleryContent.vehicle,
			thumbnail: obj.thumbnail || obj.src,
			...obj
		};
	};

	const primaryContent = galleryContent.primary
		.filter((obj) => GALLERY_TYPES.includes(obj.type))
		.map(toItem);

	const secondaryContent = galleryContent.secondary
		.filter((obj) => GALLERY_TYPES.includes(obj.type))
		.map(toItem);

	const lastContent = galleryContent.last
		.filter((obj) => GALLERY_TYPES.includes(obj.type))
		.map(toItem);

	// Splice in secondary images before the others so that the order
	// will be correct even if there are no images in the incoming array.
	imagesToDisplay.splice(1, 0, ...secondaryContent);
	imagesToDisplay.push(...lastContent);
	imagesToDisplay.unshift(...primaryContent);
};

const handleOffsetSharedVehicleImageByOne = (
	imagesToDisplay,
	accountId,
	vehicleAccountId,
	offsetSharedVehicleImageByOne,
	useStockPhotosForOffsiteInventory
) => {
	// If a vehicle's accountId does not match the widget's accountId,
	// we can assume this is a shared vehicle, and thus should not
	// include the first image, because it often features
	// an overlay featuring the wrong dealership
	if (
		accountId !== vehicleAccountId &&
		offsetSharedVehicleImageByOne === 'true' &&
		// if showing only stock photos don't offset
		useStockPhotosForOffsiteInventory !== 'true'
	) {
		imagesToDisplay.shift();
	}
};

const filterFailedImages = (imagesToDisplay, failedImages) => {
	return imagesToDisplay.filter(
		(imgObj) => !failedImages.includes(imgObj.src)
	);
};

const handleNoPhotos = (imagesToDisplay) => {
	if (imagesToDisplay.length === 0) {
		imagesToDisplay.push(NO_PHOTOS_IMAGE);
	}
};

const getImagesToDisplay = (
	images,
	galleryContent,
	accountId,
	vehicleAccountId,
	offsetSharedVehicleImageByOne,
	useStockPhotosForOffsiteInventory,
	failedImages,
	nonWiapiImageLimit = null
) => {
	let imagesToDisplay;
	// remove platform images if insertMethod is set to replace
	if (galleryContent && galleryContent.insertMethod === 'replace') {
		imagesToDisplay = [];
	} else {
		imagesToDisplay = [...images];
	}
	handleOffsetSharedVehicleImageByOne(
		imagesToDisplay,
		accountId,
		vehicleAccountId,
		offsetSharedVehicleImageByOne,
		useStockPhotosForOffsiteInventory
	);
	if (nonWiapiImageLimit !== null) {
		imagesToDisplay.splice(nonWiapiImageLimit);
	}
	if (galleryContent) {
		insertWIAPIContent(imagesToDisplay, galleryContent);
	}
	imagesToDisplay = filterFailedImages(imagesToDisplay, failedImages);
	handleNoPhotos(imagesToDisplay);
	return imagesToDisplay;
};

export const setMedia = (
	media,
	locale,
	showStockPhotosForOffsiteInventory,
	showAllStockPhotosIfNoCustomPhotos,
	accountId,
	vehicleAccountId,
	offsetSharedVehicleImageByOne,
	useStockPhotosForOffsiteInventory,
	nonWiapiImageLimit,
	stockPhotoImageSourceArray,
	showAllAvailableStockPhotos
) => {
	return (dispatch, getState) => {
		const {
			media: { failedImages }
		} = getState();
		/*
		 *  mediaStockImagesExist checks if media and for media.stockImages exist
		 *  mediaImage checks if media image exists, and media.images is array and not empty
		 *  stockPhotoDisplayOverrideExists checks for prefrences allowing to show stock photos
		 */

		const mediaImage =
			media &&
			media.images &&
			Array.isArray(media.images) &&
			media.images.length > 0 &&
			media.images[0];

		const mediaStockImagesExist =
			media &&
			media.stockImages &&
			Array.isArray(media.stockImages) &&
			media.stockImages.length > 0;

		const stockPhotoDisplayOverrideExists =
			(showAllStockPhotosIfNoCustomPhotos === 'true' &&
				mediaImage &&
				mediaImage.imageSource === 'ddc override') ||
			showAllAvailableStockPhotos === 'true';

		const getImages = () => {
			if (showStockPhotosForOffsiteInventory) {
				const vinSpecificStockImages = media.images.filter(
					(image) => image.imageType === 'VINSTOCK'
				);
				// Use VIN-specific stock images if present; otherwise, use stock images.
				return vinSpecificStockImages.length
					? vinSpecificStockImages
					: media.stockImages;
				/*
				 *  Use All stock Images available if Actual images are not available in media,
				 *  irrespective of Color and vin specification.
				 *  Checking images[0], there will be only 1 image in media.images(and that image will have an imageType of STOCK)
				 *  if no custom images present.
				 *  If media.images[0] is set to have an imageSource of ddc override, then that image is a stock photo and is the only
				 *  photo in media.images.
				 *  Images[0] needs to have an imageSource of 'ddc override' in order to filter and order by
				 *  stock photo source.
				 */
			} else if (
				mediaImage &&
				mediaStockImagesExist &&
				stockPhotoDisplayOverrideExists
			) {
				if (
					stockPhotoImageSourceArray &&
					stockPhotoImageSourceArray.length
				) {
					const stockImageList = stockPhotoImageSourceArray
						.map((imageSource) => {
							return media.stockImages.filter(
								(image) => image.imageSource === imageSource
							);
						})
						.reduce((imageArray, currentArray) => [
							...imageArray,
							...currentArray
						]);
					return [...media.images, ...stockImageList];
				}
				return [...media.images, ...media.stockImages];
			}
			return media.images;
		};

		const { videos } = media;
		const images = getImages();
		const imagesArr = [];

		/*
		 * VIN specific stock images normally doesn't has a
		 * thumbnail image src. So, we use Akamai's image resizer
		 * to generate an image.
		 * We first destructure the uri inside the thumbnail object and rename it to `thumbnail`
		 * After which we set a default if thumbnail is missing.
		 *
		 * Note: Renaming image uri to src just for the convenience of the front-end
		 * */

		images.forEach((image) => {
			const {
				uri: src,
				thumbnail: {
					uri: thumbnail = `${image.uri}?impolicy=resize&w=110`
				} = {}
			} = image;

			const imgObject = {
				src,
				thumbnail
			};

			imagesArr.push(imgObject);
		});

		/*
		 * Videos with the same content for different locales will have the same id's
		 * Filtering them prevents collision and ensures the right locale for a given user.
		 * */

		let sortedVideos = [];
		if (videos) {
			const filteredVideos = videos
				// default to 'en_US' if locale missing from video
				.filter(
					(video) => video.uri && (video.locale || 'en_US') === locale
				);
			// sort videos based on provider
			sortedVideos = sortVideos(filteredVideos);
		}

		return dispatch({
			type: SET_MEDIA,
			payload: {
				images: imagesArr,
				videos: sortedVideos,
				imagesToDisplay: getImagesToDisplay(
					imagesArr,
					null /* galleryContent */,
					accountId,
					vehicleAccountId,
					offsetSharedVehicleImageByOne,
					useStockPhotosForOffsiteInventory,
					failedImages,
					nonWiapiImageLimit
				)
			}
		});
	};
};

export function wiapiMediaUpdated(
	galleryContent,
	offsetSharedVehicleImageByOne,
	useStockPhotosForOffsiteInventory
) {
	return (dispatch, getState) => {
		const {
			widgetInfo: {
				vehicle: { accountId: vehicleAccountId },
				accountId
			},
			media: { images, failedImages }
		} = getState();

		return dispatch({
			type: SET_MEDIA,
			payload: {
				galleryContent,
				imagesToDisplay: getImagesToDisplay(
					images,
					galleryContent,
					accountId,
					vehicleAccountId,
					offsetSharedVehicleImageByOne,
					useStockPhotosForOffsiteInventory,
					failedImages,
					parseLimitFromLocation()
				)
			}
		});
	};
}

export const addFailedImages = (
	newFailedImages,
	offsetSharedVehicleImageByOne,
	useStockPhotosForOffsiteInventory
) => {
	return (dispatch, getState) => {
		const {
			widgetInfo: {
				vehicle: { accountId: vehicleAccountId },
				accountId
			},
			media: { images, galleryContent, failedImages: oldFailedImages },
			renderSettings
		} = getState();

		const mergedFailedImages = Array.from(
			new Set([...oldFailedImages, ...newFailedImages])
		);

		const imagesToDisplay = getImagesToDisplay(
			images,
			galleryContent,
			accountId,
			vehicleAccountId,
			offsetSharedVehicleImageByOne,
			useStockPhotosForOffsiteInventory,
			mergedFailedImages,
			parseLimitFromLocation()
		);

		dispatch({
			type: SET_MEDIA,
			payload: {
				failedImages: mergedFailedImages,
				imagesToDisplay
			}
		});

		// If we removed images and our index is no longer within the range of images, go to the last image.
		const newIndex = Math.min(
			imagesToDisplay.length - 1,
			renderSettings.currentIndex
		);
		if (newIndex < renderSettings.currentIndex) {
			dispatch(setRenderSettings({ currentIndex: newIndex }));
			dispatch(setModalSettings({ currentIndex: newIndex }));
		}
	};
};
